import { calculateProposalBudget, normalizeBudgetRates, BUDGET_FX_UNAVAILABLE } from '@/lib/proposalBudget';
import React, { useMemo, useState, useCallback } from 'react';
import { Building2, Plane, Sparkles, Bus, Utensils, Users, Receipt, Percent, TrendingUp, Calculator, Globe, Copy } from 'lucide-react';
import { RoomBreakdown } from './RoomBreakdown';
import { MealBreakdown } from './MealBreakdown';
import { MeetingsBreakdown } from './MeetingsBreakdown';
import { ActivitiesBreakdown } from './ActivitiesBreakdown';
import { OtherCosts } from './OtherCosts';
import { Input } from '@/components/ui/Input/Input';
import { CventProposalSidebar } from './CventProposalSidebar';
import { CopyBudgetPanel } from './CopyBudgetPanel';
import type { VenueBudgetTabProps, VenueRoomBreakdown, VenueOtherCostsFormData } from './types';
import { CURRENCY_INFO, type SupportedCurrency, SUPPORTED_CURRENCIES } from './types';

/**
 * VenueBudgetTab - A pure controlled component for venue budget editing.
 *
 * All data comes from props (venueFormData), no internal fetching.
 * Changes are reported to parent via callbacks.
 *
 * Key concepts:
 * - venueCurrency: The currency used for editing/input (per-venue)
 * - eventCurrency: The event's base currency for display in summaries
 * - All costs stored in venueCurrency, converted to eventCurrency for trip breakdown
 */
export function VenueBudgetTab({
  venueBudget,
  venueFormData,
  attendees,
  nights,
  settings,
  eventCurrency,
  exchangeRates,
  onRoomBreakdownChange,
  onActivitiesBreakdownChange,
  onMealsBreakdownChange,
  onMeetingsBreakdownChange,
  onVenueCurrencyChange,
  onOtherCostsChange,
  onBudgetNoteChange,
  otherVenues,
  onCopyBudget,
}: VenueBudgetTabProps) {
  const [showCopyPanel, setShowCopyPanel] = useState(false);

  // Venue's input currency (used for editing)
  const venueCurrency = (venueFormData.venueCurrency || 'USD') as SupportedCurrency;
  const venueCurrencyInfo = CURRENCY_INFO[venueCurrency] ?? CURRENCY_INFO.USD;

  // Event currency (used for trip breakdown display)
  const displayCurrency = (eventCurrency || 'USD') as SupportedCurrency;
  const displayCurrencyInfo = CURRENCY_INFO[displayCurrency] ?? CURRENCY_INFO.USD;

  // Format currency for venue input (in venue currency)
  const formatVenueCurrency = useCallback((amount: number | null) => {
    if (amount === null || !Number.isFinite(amount)) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: venueCurrency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }, [venueCurrency]);

  // Format currency for event display (in event currency)
  const formatEventCurrency = useCallback((amount: number | null) => {
    if (amount === null || !Number.isFinite(amount)) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: displayCurrency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }, [displayCurrency]);

  // Legacy alias for backward compatibility
  const formatCurrency = formatEventCurrency;

  // Handle room breakdown change - pass complete object to parent
  const handleRoomChange = useCallback((data: Partial<VenueRoomBreakdown>) => {
    const merged: VenueRoomBreakdown = {
      ...venueFormData.roomBreakdown,
      ...data,
    };
    onRoomBreakdownChange(merged);
  }, [venueFormData.roomBreakdown, onRoomBreakdownChange]);

  // Handle other costs change (flight, ground transport)
  const handleOtherCostChange = useCallback((field: keyof VenueOtherCostsFormData, value: number | null) => {
    onOtherCostsChange({ [field]: value });
  }, [onOtherCostsChange]);

  const dynamicTotals = useMemo(() => calculateProposalBudget({
    breakdown: { ...venueFormData, otherCosts: { ...venueFormData.otherCosts, venue_currency: venueCurrency } },
    settings, attendees, nights, eventCurrency: displayCurrency,
    exchangeRates: normalizeBudgetRates(exchangeRates),
  }), [venueFormData, venueCurrency, settings, attendees, nights, displayCurrency, exchangeRates]);

  // Line items configuration
  const lineItems = useMemo(() => [
    {
      icon: Building2,
      label: 'Accommodation',
      category: 'accommodation' as const,
      highlight: true,
      editable: false,
    },
    {
      icon: Plane,
      label: 'Flight Cost',
      category: 'flight_cost' as const,
      editable: false, // Now editable in Other Costs section
    },
    {
      icon: Sparkles,
      label: 'Activities',
      category: 'activities' as const,
      editable: false, // Now calculated from breakdown
    },
    {
      icon: Bus,
      label: 'Ground Transport',
      category: 'ground_transport' as const,
      editable: false, // Now editable in Other Costs section
    },
    {
      icon: Utensils,
      label: 'Meals',
      category: 'meals' as const,
      editable: false, // Now calculated from breakdown
    },
    {
      icon: Users,
      label: 'Meetings',
      category: 'meetings' as const,
      editable: false, // Now calculated from breakdown
    },
    {
      icon: Receipt,
      label: 'Miscellaneous',
      category: 'miscellaneous' as const,
      editable: false, // Now editable in Other Costs section
    },
  ], []);

  // Get display values for a category (in event currency for display)
  const getDisplayValues = useCallback((category: string) => {
    const amounts: Record<string, number> = {
      accommodation: dynamicTotals.accommodation, flight_cost: dynamicTotals.flightCost,
      activities: dynamicTotals.activities, ground_transport: dynamicTotals.groundTransport,
      meals: dynamicTotals.meals, meetings: dynamicTotals.meetings, miscellaneous: dynamicTotals.miscellaneous,
    };
    const amount = amounts[category];
    return { per_attendee: String(Math.round(attendees > 0 ? amount / attendees : 0)),
      subtotal: String(Math.round(amount)) };
  }, [dynamicTotals, attendees]);

  return (
    <div className="flex gap-0 h-full">
      {/* Main Content - 3/4 width */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="space-y-8">
          {!dynamicTotals.available && <p role="status" className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">{BUDGET_FX_UNAVAILABLE}</p>}
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">{venueBudget.venue_name}</h2>
              <p className="text-sm text-gray-500 mt-1">Detailed budget breakdown</p>
            </div>
            <div className="flex items-center gap-4">
              {/* Copy Budget Button */}
              {otherVenues && otherVenues.length > 0 && onCopyBudget && (
                <button
                  onClick={() => setShowCopyPanel((prev) => !prev)}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 hover:text-blue-700 hover:bg-blue-50 border border-gray-200 rounded-lg transition-colors"
                >
                  <Copy className="w-4 h-4" />
                  <span className="hidden lg:inline">Copy Budget To...</span>
                </button>
              )}
              {/* Currency Selector */}
              <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
                <Globe className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-600">Edit in:</span>
                <select
                  value={venueCurrency}
                  onChange={(e) => onVenueCurrencyChange(e.target.value)}
                  className="bg-transparent text-sm font-medium text-gray-900 focus:outline-none cursor-pointer"
                >
                  {SUPPORTED_CURRENCIES.map((curr) => (
                    <option key={curr} value={curr}>
                      {curr} ({CURRENCY_INFO[curr].symbol})
                    </option>
                  ))}
                </select>
              </div>
              <div className="bg-teal-50 rounded-lg px-4 py-2 text-center">
                <p className="text-xs text-teal-600 font-medium">Per Attendee</p>
                <p className="text-xl font-bold text-teal-700">{formatEventCurrency(Math.round(dynamicTotals.perAttendeeCost))}</p>
              </div>
              <div className="bg-secondary-600 rounded-lg px-4 py-2 text-center">
                <p className="text-xs text-white/90 font-medium">Total Program Cost</p>
                <p className="text-xl font-bold text-white">{formatEventCurrency(Math.round(dynamicTotals.totalProgramCost))}</p>
              </div>
            </div>
          </div>

          {/* Copy Budget Panel */}
          {showCopyPanel && otherVenues && otherVenues.length > 0 && onCopyBudget && (
            <CopyBudgetPanel
              sourceVenueName={venueBudget.venue_name}
              sourceCurrency={venueCurrency}
              targetVenues={otherVenues}
              onCopy={(targetIds, sections) => {
                onCopyBudget(targetIds, sections);
                setShowCopyPanel(false);
              }}
              onClose={() => setShowCopyPanel(false)}
            />
          )}

      {/* Room Breakdown Section */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
        <h3 className="font-semibold text-amber-900 mb-4 flex items-center gap-2">
          <Building2 className="w-5 h-5" />
          Room Breakdown
          <span className="ml-auto text-sm font-normal text-amber-700">({venueCurrencyInfo.symbol} {venueCurrency})</span>
        </h3>
        <RoomBreakdown
          shortlistId={venueFormData.shortlistId}
          breakdown={venueFormData.roomBreakdown}
          nights={nights}
          displayCurrency={venueCurrency}
          onChange={handleRoomChange}
        />
      </div>

      {/* Meal Breakdown Section */}
      <MealBreakdown
        shortlistId={venueFormData.shortlistId}
        breakdown={venueFormData.mealsBreakdown}
        attendees={attendees}
        displayCurrency={venueCurrency}
        onChange={(data) => onMealsBreakdownChange(data)}
      />

      {/* Meetings Breakdown Section */}
      <MeetingsBreakdown
        shortlistId={venueFormData.shortlistId}
        breakdown={venueFormData.meetingsBreakdown}
        attendees={attendees}
        displayCurrency={venueCurrency}
        onChange={(data) => onMeetingsBreakdownChange(data)}
      />

      {/* Other Costs Section */}
      <OtherCosts
        shortlistId={venueFormData.shortlistId}
        otherCosts={venueFormData.otherCosts}
        activitiesBreakdown={venueFormData.activitiesBreakdown}
        attendees={attendees}
        displayCurrency={venueCurrency}
        onOtherCostsChange={onOtherCostsChange}
        onActivitiesChange={(data) => onActivitiesBreakdownChange(data)}
      />

      {/* Budget Note */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="mb-2">
          <label className="block text-sm font-medium text-gray-900 mb-1">
            Budget Note
          </label>
          <p className="text-xs text-gray-500">Displayed on the proposal budget section for this venue, between line items and totals</p>
        </div>
        <textarea
          value={venueFormData.otherCosts?.budget_note || ''}
          onChange={(e) => onBudgetNoteChange(e.target.value || null)}
          placeholder="Add a note to display on the budget section of the proposal for this venue..."
          maxLength={2000}
          rows={3}
          className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-y"
        />
      </div>

      {/* Trip Breakdown Table - Read Only (displayed in Event Currency) */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Trip Breakdown</h3>
          <span className="text-sm text-gray-500">
            Displayed in {displayCurrencyInfo.name} ({displayCurrencyInfo.symbol})
          </span>
        </div>

        <div className="divide-y divide-gray-100">
          {/* Header Row */}
          <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50 text-sm font-medium text-gray-500">
            <div className="col-span-5">Category</div>
            <div className="col-span-3 text-right">Per Attendee ({displayCurrencyInfo.symbol})</div>
            <div className="col-span-4 text-right">Subtotal ({displayCurrencyInfo.symbol})</div>
          </div>

          {/* Line Items */}
          {lineItems.map(({ icon: Icon, label, category, highlight }) => {
            const displayValues = getDisplayValues(category);
            const item = venueBudget[category as keyof typeof venueBudget] as { per_attendee: number | null; subtotal: number; details?: string };

            return (
              <React.Fragment key={category}>
                <div
                  className={`grid grid-cols-12 gap-4 px-6 py-4 ${highlight ? 'bg-amber-50' : ''}`}
                >
                  <div className="col-span-5 flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${highlight ? 'bg-amber-200' : 'bg-gray-100'}`}>
                      <Icon className={`w-4 h-4 ${highlight ? 'text-amber-700' : 'text-gray-600'}`} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{label}</p>
                      {item.details && (
                        <p className="text-xs text-gray-500">{item.details}</p>
                      )}
                    </div>
                  </div>
                  <div className="col-span-3 text-right">
                    <span className="text-gray-900">
                      {formatCurrency(parseFloat(displayValues.per_attendee))}
                    </span>
                  </div>
                  <div className="col-span-4 text-right">
                    <span className="font-medium text-gray-900">
                      {formatCurrency(parseFloat(displayValues.subtotal))}
                    </span>
                  </div>
                </div>
              </React.Fragment>
            );
          })}

          {/* Subtotal */}
          <div className="grid grid-cols-12 gap-4 px-6 py-4 bg-gray-50">
            <div className="col-span-8 flex items-center">
              <Calculator className="w-4 h-4 text-gray-500 mr-2" />
              <span className="font-semibold text-gray-700">Subtotal</span>
            </div>
            <div className="col-span-4 text-right">
              <span className="font-bold text-gray-900">{formatCurrency(dynamicTotals.subtotal)}</span>
            </div>
          </div>

          {/* Contingency */}
          <div className="grid grid-cols-12 gap-4 px-6 py-4">
            <div className="col-span-8 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-blue-100">
                <Percent className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Contingency</p>
                <p className="text-xs text-gray-500">{settings.contingency_fixed != null ? 'Fixed amount' : `${settings.contingency_percent}% of subtotal excl. flights`}</p>
              </div>
            </div>
            <div className="col-span-4 text-right flex items-center justify-end">
              <span className="font-medium text-gray-900">{formatCurrency(dynamicTotals.contingencyAmount)}</span>
            </div>
          </div>

          {/* Planning Overhead */}
          <div className="grid grid-cols-12 gap-4 px-6 py-4">
            <div className="col-span-8 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-purple-100">
                <TrendingUp className="w-4 h-4 text-purple-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Moniker Professional Planning Overhead</p>
                <p className="text-xs text-gray-500">
                  {settings.planning_overhead_fixed !== null
                    ? `Fixed: ${formatCurrency(settings.planning_overhead_fixed / 100)}`
                    : `${settings.planning_overhead_percent}%`}
                </p>
              </div>
            </div>
            <div className="col-span-4 text-right flex items-center justify-end">
              <span className="font-medium text-gray-900">{formatCurrency(dynamicTotals.planningOverheadAmount)}</span>
            </div>
          </div>

          {/* Profit Margin */}
          <div className="grid grid-cols-12 gap-4 px-6 py-4">
            <div className="col-span-8 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-green-100">
                <TrendingUp className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Moniker Profit Margin</p>
                <p className="text-xs text-gray-500">
                  {settings.profit_margin_fixed !== null
                    ? `Fixed: ${formatCurrency(settings.profit_margin_fixed / 100)}`
                    : `${settings.profit_margin_percent}%`}
                </p>
              </div>
            </div>
            <div className="col-span-4 text-right flex items-center justify-end">
              <span className="font-medium text-gray-900">{formatCurrency(dynamicTotals.profitMarginAmount)}</span>
            </div>
          </div>

          {/* Total */}
          <div className="grid grid-cols-12 gap-4 px-6 py-5 bg-secondary-600">
            <div className="col-span-8">
              <p className="font-bold text-lg text-white">Total Program Cost ({displayCurrency})</p>
            </div>
            <div className="col-span-4 text-right">
              <span className="font-bold text-2xl text-white">{formatCurrency(dynamicTotals.totalProgramCost)}</span>
            </div>
          </div>
        </div>
      </div>
        </div>
      </div>

      {/* Sidebar - ~1/4 width, responsive */}
      <div className="w-80 xl:w-96 flex-shrink-0">
        <CventProposalSidebar shortlistId={venueFormData.shortlistId} venueName={venueBudget.venue_name} />
      </div>
    </div>
  );
}

export default VenueBudgetTab;
